import React, { useState, useEffect, useMemo } from 'react';
import adminService from "../../services/adminService";

// petit helper: chercher une valeur dans plusieurs chemins possibles
const pick = (obj, paths, fallback = null) => {
  for (const p of paths) {
    const v = p.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

// normaliser string (ville)
const norm = (s) => String(s || "").trim().toLowerCase();

const AssignHotelModal = ({ reservation, onClose, onAssigned }) => {
  const reservationId = reservation?._id;

  // ✅ Récupération souple des critères depuis la réservation
  const ville = pick(reservation, ["ville", "hotelVille", "destination.ville"], "");
  const etoiles = pick(
    reservation,
    ["nb_etoiles", "etoiles", "categorieEtoiles", "categorie", "hotel.nb_etoiles", "hotel.etoiles"],
    ""
  );
  const nbPersonnes = pick(
    reservation,
    ["nb_personnes", "nombrePersonnes", "nbInvites", "nombreAdultes", "totalPersons"],
    ""
  );

  // budget si tu as un champ (sinon laisse null)
  const budgetMin = pick(reservation, ["prixParNuit.min", "prix_min", "budgetMin"], null);
  const budgetMax = pick(reservation, ["prixParNuit.max", "prix_max", "budgetMax"], null);

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedHotelId, setSelectedHotelId] = useState("");

  // ✅ Filters envoyés au backend si la route /admin/hotels/match existe
  const filters = useMemo(() => {
    const f = {};
    if (ville) f.ville = ville;
    if (etoiles !== "" && etoiles !== null && etoiles !== undefined) f.nb_etoiles = Number(etoiles);
    if (nbPersonnes !== "" && nbPersonnes !== null && nbPersonnes !== undefined) f.nb_personnes = Number(nbPersonnes);
    if (budgetMin !== null && budgetMin !== undefined && budgetMin !== "") f.minPrice = Number(budgetMin);
    if (budgetMax !== null && budgetMax !== undefined && budgetMax !== "") f.maxPrice = Number(budgetMax);
    return f;
  }, [ville, etoiles, nbPersonnes, budgetMin, budgetMax]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // 1) tenter endpoint backend (si tu l’as implémenté)
        try {
          const res = await adminService.getMatchingHotels(filters);
          const list = res?.data?.items || res?.items || [];
          if (Array.isArray(list) && list.length > 0) {
            setHotels(list);
            return;
          }
        } catch (eMatch) {
          // si route inexistante / erreur -> fallback front
          console.warn("getMatchingHotels failed, fallback front filter:", eMatch?.message || eMatch);
        }

        // 2) fallback: charger tous les hôtels et filtrer côté front (robuste)
        const res2 = await adminService.getAllItems("hotels", { page: 1, limit: 500, search: "" });
        const all = res2?.data?.items || [];

        const filtered = all.filter((h) => {
          const hVille = pick(h, ["ville", "city", "adresse.ville"], "");
          const hStars = pick(h, ["nb_etoiles", "etoiles", "stars"], null);
          const hCap = pick(h, ["nb_personnes", "capacite", "capacity", "nbPlaces"], null);
          const hPrice = pick(h, ["prix", "prix_nuit", "pricePerNight", "price"], null);

          const okVille = ville ? norm(hVille) === norm(ville) : true;

          const okStars = etoiles !== "" ? Number(hStars) === Number(etoiles) : true;

          // capacité: on accepte si hôtel >= nbPersonnes (si info existe)
          const okCap =
            nbPersonnes !== ""
              ? (hCap === null || hCap === undefined ? true : Number(hCap) >= Number(nbPersonnes))
              : true;

          const okMin =
            budgetMin !== null
              ? (hPrice === null || hPrice === undefined ? true : Number(hPrice) >= Number(budgetMin))
              : true;

          const okMax =
            budgetMax !== null
              ? (hPrice === null || hPrice === undefined ? true : Number(hPrice) <= Number(budgetMax))
              : true;

          return okVille && okStars && okCap && okMin && okMax;
        });

        setHotels(filtered);
      } catch (e) {
        console.error(e);
        setHotels([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [reservationId, filters, ville, etoiles, nbPersonnes, budgetMin, budgetMax]); // ✅ important: recharge quand les filtres changent

  const assign = async () => {
    if (!selectedHotelId) return alert("Sélectionne un hôtel");

    try {
      await adminService.assignHotelToReservation(reservationId, selectedHotelId);
      alert("Hôtel assigné ✅");
      await onAssigned?.();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Erreur lors de l'assignation");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[85] flex items-center justify-center p-4 pt-20 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">Assigner un hôtel</h3>
              <p className="text-xs text-white/90">
                Filtre: {ville ? `ville=${ville}` : "ville=—"} | {etoiles ? `etoiles=${etoiles}` : "etoiles=—"} |{" "}
                {nbPersonnes ? `nb=${nbPersonnes}` : "nb=—"}
              </p>
              <p className="text-[11px] text-white/80 mt-1">
                (Debug) {Object.keys(filters).length ? JSON.stringify(filters) : "Aucun filtre envoyé"}
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:text-slate-200">
              ✖
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-600">Chargement des hôtels...</div>
          ) : hotels.length === 0 ? (
            <div className="p-6 text-center text-slate-600">
              Aucun hôtel ne correspond aux critères. (Vérifie surtout **ville** et **étoiles**)
            </div>
          ) : (
            <div className="space-y-3">
              {hotels.map((h) => {
                const hStars = pick(h, ["nb_etoiles", "etoiles", "stars"], "—");
                const hCap = pick(h, ["nb_personnes", "capacite", "capacity", "nbPlaces"], "—");
                const hPrice = pick(h, ["prix", "prix_nuit", "pricePerNight", "price"], "—");

                return (
                  <label
                    key={h._id}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${
                      selectedHotelId === h._id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="hotel"
                      checked={selectedHotelId === h._id}
                      onChange={() => setSelectedHotelId(h._id)}
                      className="h-4 w-4 accent-orange-600"
                    />

                    <div className="flex-1">
                      <div className="font-bold text-slate-900">{h.nom || "Hôtel"}</div>
                      <div className="text-sm text-slate-600">
                        {h.ville || "—"} • {hStars !== "—" ? `${hStars}⭐` : "—"} •{" "}
                        {hCap !== "—" ? `capacité ${hCap}` : "capacité —"} •{" "}
                        {hPrice !== "—" ? `${hPrice} MAD/nuit` : "prix —"}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">ID: {h._id}</div>
                    </div>

                    {h.image && (
                      <img
                        src={h.image.startsWith("http") ? h.image : `https://nouzhaback-theta.vercel.app${h.image}`}
                        alt="hotel"
                        className="w-24 h-16 object-cover rounded-lg border border-slate-200"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-slate-50 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={assign}
            disabled={!selectedHotelId}
            className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            ✅ Assigner cet hôtel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignHotelModal;
